import { loadFirebaseAuth } from "@/config/firebase-auth";
import { isConfigured } from "@/config/firebase-core";

// Lazy-loaded auth instance and provider
let cachedAuth: import("firebase/auth").Auth | null = null;
let cachedGoogleProvider: import("firebase/auth").GoogleAuthProvider | null = null;

const getAuthInstance = async () => {
  if (cachedAuth) return cachedAuth;
  const { auth } = await loadFirebaseAuth();
  cachedAuth = auth;
  return auth;
};

const getGoogleProvider = async () => {
  if (cachedGoogleProvider) return cachedGoogleProvider;
  const { firebaseAuth } = await loadFirebaseAuth();
  cachedGoogleProvider = new firebaseAuth.GoogleAuthProvider();
  return cachedGoogleProvider;
};

// Export getter for auth (for backwards compatibility where sync access is needed)
export const getAuth = getAuthInstance;

// Re-export isConfigured for backwards compatibility
export { isConfigured };

export const auth_signInGoogle = async () => {
  if (!isConfigured) {
    const mockUser = {
      uid: "mock-user-id",
      displayName: "Mock User",
      email: "mock@example.com",
      photoURL: "https://ui-avatars.com/api/?name=Mock+User"
    };
    return mockUser;
  }

  const { firebaseAuth } = await loadFirebaseAuth();
  const auth = await getAuthInstance();
  const provider = await getGoogleProvider();

  if (!auth) {
    throw new Error("Auth not initialized");
  }

  try {
    const result = await firebaseAuth.signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Google Sign In Error", error);
    throw error;
  }
};

export const auth_signInEmail = async (email: string, pass: string) => {
  if (!isConfigured) {
    throw new Error("Email auth requires configured Firebase. Use Google Mock Login instead.");
  }

  const { firebaseAuth } = await loadFirebaseAuth();
  const auth = await getAuthInstance();

  if (!auth) {
    throw new Error("Auth not initialized");
  }

  const cred = await firebaseAuth.signInWithEmailAndPassword(auth, email, pass);
  return cred.user;
};

export const auth_signUpEmail = async (email: string, pass: string) => {
  if (!isConfigured) {
    throw new Error("Email auth requires configured Firebase. Use Google Mock Login instead.");
  }

  const { firebaseAuth } = await loadFirebaseAuth();
  const auth = await getAuthInstance();

  if (!auth) {
    throw new Error("Auth not initialized");
  }

  const cred = await firebaseAuth.createUserWithEmailAndPassword(auth, email, pass);
  return cred.user;
};

export const auth_logout = async () => {
  if (!isConfigured) {
    console.warn("Mock Logout");
    return;
  }

  const { firebaseAuth } = await loadFirebaseAuth();
  const auth = await getAuthInstance();

  if (!auth) {
    return;
  }

  await firebaseAuth.signOut(auth);
};

export const updateUserProfile = async (
  userId: string,
  updates: { displayName?: string; photoURL?: string },
) => {
  if (!isConfigured) {
    console.warn("Mock Update Profile", updates);
    return;
  }

  const { firebaseAuth } = await loadFirebaseAuth();
  const auth = await getAuthInstance();

  if (auth?.currentUser) {
    await firebaseAuth.updateProfile(auth.currentUser, updates);
  } else {
    console.warn("No current user to update profile");
  }
};
